import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, CheckCheck, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { ROUTES } from '@/shared/config/routes';
import {
  useUnreadCount,
  useRecentNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
} from '@/shared/hooks/useNotifications';
import type { Notification } from '@/shared/services/notificationService';

function NotificationItem({ notification, onRead, onClose }: { notification: Notification; onRead: (id: string) => void; onClose: () => void }) {
  const isUnread = !notification.read_at;
  const navigate = useNavigate();

  const handleClick = () => {
    if (isUnread) {
      onRead(notification.id);
    }
    // Navigate to related entity if available
    if (notification.entity_type && notification.entity_id) {
      const entityRoutes: Record<string, string> = {
        ncr_record: ROUTES.QUALITY_ISSUES,
        document: ROUTES.DOCUMENTS,
        audit: ROUTES.AUDITS,
        risk: ROUTES.RISKS,
        equipment: ROUTES.EQUIPMENT,
        change_request: ROUTES.CHANGE_MANAGEMENT,
        complaint: ROUTES.COMPLAINTS,
        supplier: ROUTES.SUPPLIERS,
        report_job: ROUTES.REPORTS,
      };
      const base = entityRoutes[notification.entity_type];
      if (base) {
        onClose();
        void navigate(`${base}/${notification.entity_id}`);
      }
    }
  };

  const timeAgo = getTimeAgo(notification.created_at);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`flex items-start gap-3 w-full px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted ${
        isUnread ? 'bg-primary/5' : ''
      }`}
    >
      {isUnread && (
        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
      )}
      {!isUnread && <span className="mt-1.5 h-2 w-2 shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className={`text-sm truncate ${isUnread ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
          {notification.title}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
          {notification.body as string}
        </p>
        <p className="text-xs text-muted-foreground/60 mt-1">{timeAgo}</p>
      </div>
    </button>
  );
}

function getTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${String(minutes)}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)}h ago`;
  const days = Math.floor(hours / 24);
  return `${String(days)}d ago`;
}

export function NotificationBell() {
  const { t } = useTranslation('notifications');
  const { data: unreadCount } = useUnreadCount();
  const { data: notifications } = useRecentNotifications(15);
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const count = unreadCount ?? 0;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={t('bell_label')}
        >
          <Bell className="h-5 w-5" strokeWidth={1.5} />
          {count > 0 && (
            <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-danger text-[10px] font-bold text-white">
              {count > 9 ? '9+' : String(count)}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] p-0 sm:w-80">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <h3 className="text-sm font-semibold">{t('title')}</h3>
          {count > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => { markAllAsRead.mutate(); }}
            >
              <CheckCheck className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
              {t('mark_all_read')}
            </Button>
          )}
        </div>

        {/* Notification list */}
        <div className="max-h-80 overflow-y-auto">
          {(!notifications || notifications.length === 0) ? (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              <Bell className="h-8 w-8 text-muted-foreground/40 mb-2" strokeWidth={1.5} />
              <p className="text-sm text-muted-foreground">{t('empty_state')}</p>
              <p className="text-xs text-muted-foreground/60 mt-1">{t('empty_state_description')}</p>
            </div>
          ) : (
            notifications.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onRead={(id) => { markAsRead.mutate(id); }}
                onClose={() => { setOpen(false); }}
              />
            ))
          )}
        </div>

        {/* Footer */}
        {notifications && notifications.length > 0 && (
          <div className="border-t border-border p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={() => {
                setOpen(false);
                void navigate(ROUTES.SETTINGS.NOTIFICATIONS);
              }}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
              {t('view_all')}
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
